// src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import PromptManagement from '../components/PromptManagement';
import SchemaManagement from '../components/SchemaManagement';
import UserManagement from '../components/UserManagement';
import AgentManagement from '../components/AgentManagement';
import PolicyManagement from '../components/PolicyManagement';
import RoleManagement from '../components/RoleManagement';
import UserRoleAssignment from '../components/UserRoleAssignment';
import PolicyManagementNew from '../components/PolicyManagementNew';
import DocsManagement from '../components/Docs/DocsManagement';
import DBIConfigManagement from '../components/DBIConfigManagement';
import ProposedQuestionsManagement from '../components/ProposedQuestionsManagement';
import { Database, FileText, Users, Zap, Shield, BookOpen, CalendarClock } from 'lucide-react';
import ScheduledReportsManagement from '../components/ScheduledReportsManagement';
import QueryLog from '../components/QueryLog';

const AdminDashboard = () => {
  const { isAdmin, isAIManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeAccessControl, setActiveAccessControl] = useState('policies');
  const [activeAccessControlNew, setActiveAccessControlNew] = useState('policies'); // Add state for new AC
  const [activeShootTab, setActiveShootTab] = useState('schema');

  // Get the current section from the URL
  const currentPath = location.pathname.split('/').pop();

  // Check if user has access (either admin or AI Manager)
  const hasAccess = isAdmin || isAIManager;

  useEffect(() => {
  }, [isAdmin, isAIManager]);
  
  useEffect(() => {
    // If user doesn't have access, redirect to home
    if (!hasAccess) {
      navigate('/');
      return;
    }
    
    // If no section is specified
    if (location.pathname === '/admin') {
      // AI Managers go directly to prompts, Admins go to schema
      if (isAIManager && !isAdmin) {
        navigate('/admin/shoot/prompts');
        setActiveShootTab('prompts');
      } else {
        navigate('/admin/shoot/schema');
        setActiveShootTab('schema');
      }
    }
    
    // Sync activeShootTab with current path
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (['schema', 'prompts', 'shoot-config', 'proposed-questions', 'scheduled-reports', 'log'].includes(lastSegment)) {
      setActiveShootTab(lastSegment);
    }
  }, [location, navigate, hasAccess, isAdmin, isAIManager]);

  // If no access, don't render anything
  if (!hasAccess) {
    return null;
  }

  const renderAccessControlContent = () => {
    switch (activeAccessControl) {
      case 'policies':
        return <PolicyManagement />;
      case 'roles':
        return <RoleManagement />;
      case 'user-roles':
        return <UserRoleAssignment />;
      default:
        return <PolicyManagement />;
    }
  };

  const renderAccessControlNewContent = () => {
    switch (activeAccessControlNew) {
      case 'policies':
        return <PolicyManagementNew />;
      case 'roles':
        return <RoleManagement policyFilter={{ accessMethod: 'wrapper' }} />;
      case 'user-roles':
        return <UserRoleAssignment policyFilter={{ accessMethod: 'wrapper' }} />;
      default:
        return <PolicyManagementNew />;
    }
  };

  const renderShootContent = () => {
    // AI Managers can see prompts and logs
    if (isAIManager && !isAdmin) {
      switch (activeShootTab) {
        case 'prompts':
          return <PromptManagement isAIManager={true} />;
        case 'log':
          return <QueryLog />;
        default:
          return <PromptManagement isAIManager={true} />;
      }
    }

    // Admins see everything
    switch (activeShootTab) {
      case 'schema':
        return <SchemaManagement />;
      case 'prompts':
        return <PromptManagement isAIManager={false} />;
      case 'shoot-config':
        return <DBIConfigManagement />;
      case 'proposed-questions':
        return <ProposedQuestionsManagement />;
      case 'scheduled-reports':
        return <ScheduledReportsManagement />;
      case 'log':
        return <QueryLog />;
      default:
        return <SchemaManagement />;
    }
  };

  const getTabClass = (tabName) => {
    // For main tabs
    if (!tabName.includes('/')) {
      return `pb-4 px-2 -mb-px ${
        currentPath === tabName || (tabName === 'shoot' && ['schema', 'prompts', 'shoot-config', 'proposed-questions', 'scheduled-reports', 'log'].includes(currentPath))
          ? 'border-b-2 border-primary text-primary font-medium'
          : 'text-gray-500 hover:text-gray-700'
      }`;
    }
  };

  const getShootTabClass = (tabName) => {
    return `relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all ${
      activeShootTab === tabName ? 'text-primary' : 'text-gray-600 hover:text-gray-900'
    }`;
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="container mx-auto px-2 sm:px-0 py-2 sm:py-4">
        <div className="mb-6">
          <nav className="flex space-x-6 border-b border-gray-200">
            {/* Always show Shoot tab for both Admin and AI Manager */}
            <button 
              className={getTabClass('shoot')}
              onClick={() => {
                if (isAIManager && !isAdmin) {
                  navigate('/admin/shoot/prompts');
                  setActiveShootTab('prompts');
                } else {
                  navigate('/admin/shoot/schema');
                  setActiveShootTab('schema');
                }
              }}
            >
              Shoot
            </button>
            
            {/* Show Users tab for both Admins and AI Managers */}
            {(isAdmin || isAIManager) && (
              <button 
                className={getTabClass('users')}
                onClick={() => navigate('/admin/users')}
              >
                Users
              </button>
            )}
            
            {/* Only show these tabs for Admins */}
            {isAdmin && (
              <>
                <button 
                  className={getTabClass('docs')}
                  onClick={() => navigate('/admin/docs')}
                >
                  Docs
                </button>
                <button 
                  className={getTabClass('agents')}
                  onClick={() => navigate('/admin/agents')}
                >
                  Agents
                </button>
                <button 
                  className={getTabClass('access-control')}
                  onClick={() => navigate('/admin/access-control')}
                >
                  Access Control
                </button>
                <button 
                  className={getTabClass('access-control-new')}
                  onClick={() => navigate('/admin/access-control-new')}
                >
                  Access Control (New)
                </button>
              </>
            )}
          </nav>
        </div>
        
        {/* Main content area */}
        <div className="w-full">
          <Routes>
            {/* New shoot subpages routes */}
            <Route 
              path="shoot/*" 
              element={
                <div>
                  {/* Only show sub-tabs for Admins */}
                  {isAdmin && (
                    <div className="border-b border-gray-200 mb-6">
                      <div className="flex items-end gap-6">
                        <button
                          onClick={() => {
                            setActiveShootTab('schema');
                            navigate('/admin/shoot/schema');
                          }}
                          className={getShootTabClass('schema')}
                        >
                          <Database className="h-4 w-4" />
                          Data
                          {activeShootTab === 'schema' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('prompts');
                            navigate('/admin/shoot/prompts');
                          }}
                          className={getShootTabClass('prompts')}
                        >
                          <FileText className="h-4 w-4" />
                          Prompt
                          {activeShootTab === 'prompts' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('shoot-config');
                            navigate('/admin/shoot/shoot-config');
                          }}
                          className={getShootTabClass('shoot-config')}
                        >
                          <BookOpen className="h-4 w-4" />
                          Shoot Config
                          {activeShootTab === 'shoot-config' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('proposed-questions');
                            navigate('/admin/shoot/proposed-questions');
                          }}
                          className={getShootTabClass('proposed-questions')}
                        >
                          <Zap className="h-4 w-4" />
                          Proposed Questions
                          {activeShootTab === 'proposed-questions' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('scheduled-reports');
                            navigate('/admin/shoot/scheduled-reports');
                          }}
                          className={getShootTabClass('scheduled-reports')}
                        >
                          <CalendarClock className="h-4 w-4" />
                          Scheduled Reports
                          {activeShootTab === 'scheduled-reports' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('log');
                            navigate('/admin/shoot/log');
                          }}
                          className={getShootTabClass('log')}
                        >
                          <FileText className="h-4 w-4" />
                          Log
                          {activeShootTab === 'log' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* AI Managers see a simplified header with tabs */}
                  {isAIManager && !isAdmin && (
                    <div className="mb-6">
                      <div className="border-b border-gray-200 pb-4 mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">AI Management</h2>
                        <p className="text-sm text-gray-500 mt-1">Configure business rules and monitor query logs</p>
                      </div>
                      <div className="flex items-end gap-6 border-b border-gray-200">
                        <button
                          onClick={() => {
                            setActiveShootTab('prompts');
                            navigate('/admin/shoot/prompts');
                          }}
                          className={getShootTabClass('prompts')}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Prompts
                          {activeShootTab === 'prompts' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setActiveShootTab('log');
                            navigate('/admin/shoot/log');
                          }}
                          className={getShootTabClass('log')}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Log
                          {activeShootTab === 'log' && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {renderShootContent()}
                </div>
              } 
            />
            
            {/* Route to go to the default shoot tab */}
            <Route path="shoot" element={
              isAIManager && !isAdmin 
                ? <Navigate to="/admin/shoot/prompts" replace />
                : <Navigate to="/admin/shoot/schema" replace />
            } />
            
            {/* Preserving old direct routes for compatibility - only for Admins */}
            {isAdmin && (
              <>
                <Route path="prompts" element={<Navigate to="/admin/shoot/prompts" replace />} />
                <Route path="schema" element={<Navigate to="/admin/shoot/schema" replace />} />
                <Route path="shoot-config" element={<Navigate to="/admin/shoot/shoot-config" replace />} />
                <Route path="proposed-questions" element={<Navigate to="/admin/shoot/proposed-questions" replace />} />
                <Route path="scheduled-reports" element={<Navigate to="/admin/shoot/scheduled-reports" replace />} />
                <Route path="docs" element={<DocsManagement />} />
                <Route path="agents" element={<AgentManagement />} />
                <Route 
                  path="access-control" 
                  element={
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex space-x-4 border-b border-gray-200">
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControl === 'policies' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControl('policies')}
                          >
                            Policies
                          </button>
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControl === 'roles' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControl('roles')}
                          >
                            Roles
                          </button>
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControl === 'user-roles' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControl('user-roles')}
                          >
                            User Roles
                          </button>
                        </div>
                        
                        <div className="mt-6">
                          {renderAccessControlContent()}
                        </div>
                      </div>
                    </div>
                  } 
                />
                <Route 
                  path="access-control-new" 
                  element={
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-md p-3">
                          <p className="text-sm text-purple-800">
                            <strong>CTE Wrapper Access Control:</strong> This new approach uses dynamic security wrappers 
                            instead of materialized views. Policies are applied in real-time during query execution.
                          </p>
                        </div>
                        <div className="flex space-x-4 border-b border-gray-200">
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControlNew === 'policies' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControlNew('policies')}
                          >
                            Policies
                          </button>
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControlNew === 'roles' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControlNew('roles')}
                          >
                            Roles
                          </button>
                          <button 
                            className={`pb-4 px-2 -mb-px ${
                              activeAccessControlNew === 'user-roles' 
                                ? 'border-b-2 border-primary text-primary font-medium'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveAccessControlNew('user-roles')}
                          >
                            User Roles
                          </button>
                        </div>
                        
                        <div className="mt-6">
                          {renderAccessControlNewContent()}
                        </div>
                      </div>
                    </div>
                  } 
                />
              </>
            )}
            
            {/* Routes accessible to both Admins and AI Managers */}
            {(isAdmin || isAIManager) && (
              <Route path="users" element={<UserManagement />} />
            )}
            
            {/* AI Managers get redirected to prompts */}
            {isAIManager && !isAdmin && (
              <Route path="*" element={<Navigate to="/admin/shoot/prompts" replace />} />
            )}
            
            <Route path="/" element={
              isAIManager && !isAdmin 
                ? <Navigate to="/admin/shoot/prompts" replace />
                : <Navigate to="/admin/shoot/schema" replace />
            } />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;